import PropTypes from 'prop-types';
import { createContext, useMemo } from 'react';
import useReportController, {
  formatRangeDates,
  getDefaultPastMonthRange,
} from '../hooks/useReportController';
import { getCoProductionReport } from '../services/reports';
import { DEFAULT_PRODUCT_CURRENCY_CODE, DOLARIZATION_SUPPORTED_CURRENCIES } from '../utils/currency';

export const ReportCoProductionsContext = createContext({
  table: {},
  coProductionReport: [],
  countCoProduction: 1,
  setType: () => {},
  dates: {},
  setDates: () => {},
  currency: DEFAULT_PRODUCT_CURRENCY_CODE,
  setCurrency: () => {},
  currencyOptions: DOLARIZATION_SUPPORTED_CURRENCIES,
  mutateExport: () => {},
  loadingExport: false,
  fetchingCoProduction: false,
});

const ReportCoProductionsProvider = ({ children }) => {
  const {
    table,
    data,
    fetching: fetchingCoProduction,
    dates,
    setDates,
    type,
    setType,
    currency,
    setCurrency,
    currencyOptions,
    mutateExport,
    loadingExport,
  } = useReportController({
    report: 'coproduction',
    getDefaultDates: getDefaultPastMonthRange,
    formatDates: formatRangeDates,
    tableFilterOptions: { defaultCurrentPage: 1 },
    defaultType: 'received',
    buildQueryKey: ({ page, type: currentType, dates: formattedDates, currency: apiCurrency }) => [
      'reports/coproduction',
      page,
      currentType,
      formattedDates,
      apiCurrency,
    ],
    fetchReport: ({ page, type: currentType, dates: formattedDates, currency: apiCurrency }) =>
      getCoProductionReport({
        page,
        type: currentType,
        dates: formattedDates,
        currency: apiCurrency,
      }),
    initialData: { results: [], count: 0 },
    buildExportParams: (args) => ({ extension: args.extension, type: args.type }),
    invalidateQueryKey: ['reports/coproduction'],
  });

  const { results: coProductionReport = [], count: countCoProduction } = data;

  const value = useMemo(
    () => ({
      coProductionReport,
      countCoProduction,
      fetchingCoProduction,
      table,
      type,
      setType,
      dates,
      setDates,
      currency,
      setCurrency,
      currencyOptions,
      mutateExport,
      loadingExport,
    }),
    [
      coProductionReport,
      countCoProduction,
      fetchingCoProduction,
      table,
      type,
      setType,
      dates,
      setDates,
      currency,
      setCurrency,
      currencyOptions,
      mutateExport,
      loadingExport,
    ]
  );

  return (
    <ReportCoProductionsContext.Provider value={value}>
      {children}
    </ReportCoProductionsContext.Provider>
  );
};

ReportCoProductionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ReportCoProductionsProvider;
